// ============================================================
// GitTags.jsx
//
// A branded, responsive course content page for the
// "Git Tags & Releases" lesson.
//
// Author: ProDev Enterprise Series (DevOps Branch)
// Features: Lightweight vs Annotated tags, remote syncing,
//           and GitHub Release orchestration logic.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import LocalOfferIcon         from "@mui/icons-material/LocalOffer";
import NewReleasesIcon        from "@mui/icons-material/NewReleases";
import CloudUploadIcon        from "@mui/icons-material/CloudUpload";
import DeleteSweepIcon        from "@mui/icons-material/DeleteSweep";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import InfoIcon               from "@mui/icons-material/Info";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function GitTags() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<LocalOfferIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="DevOps · Versioning & Releases"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Git Tags & <span>Project Releases</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Capture specific moments in your project history. Tags serve as immutable checkpoints—commonly used to mark production releases like v1.0.0—ensuring you can always reference a stable version of your code.
                    </Typography>
                </Box>

                {/* 1. TAG TYPES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<InfoIcon />} label="1. Lightweight vs Annotated" />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1.5 }}>Lightweight Tags</Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 13, mb: 2 }}>A simple pointer to a commit. Think of it as a bookmark that doesn't change.</Typography>
                                <Typography sx={{ color: BRAND.codeText, fontFamily: 'monospace', fontSize: 11 }}>git tag v1.0-lw</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.gold}`, borderRadius: 2, height: '100%' }}>
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 700, mb: 1.5 }}>Annotated Tags (Recommended)</Typography>
                                <Typography sx={{ color: BRAND.white, fontSize: 13, mb: 2 }}>Stored as full objects in the Git DB. Includes tagger name, email, date, and a message.</Typography>
                                <Typography sx={{ color: BRAND.codeText, fontFamily: 'monospace', fontSize: 11 }}>git tag -a v1.0 -m "Release version 1.0"</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 2. SYNCING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CloudUploadIcon />} label="2. Synchronizing with Remote" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        By default, <code>git push</code> does not transfer tags to remote servers. You must explicitly push them.
                    </Typography>
                    <CodeBlock
                        title="Remote Tag Commands"
                        code={`# Push a specific tag\ngit push origin v1.0\n\n# Push all local tags to remote\ngit push origin --tags`}
                    />
                </Box>

                {/* 3. MANAGEMENT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="3. Tag Management" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        List existing tags or remove them if they were created by mistake. Note that deleting a remote tag requires a specific push command.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <CodeBlock
                                title="Listing & Viewing"
                                code={`# List all tags\ngit tag\n\n# View tag metadata and commit\ngit show v1.0`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CodeBlock
                                title="Deletion"
                                code={`# Delete local tag\ngit tag -d v1.0\n\n# Delete remote tag\ngit push origin --delete v1.0`}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* 4. GITHUB RELEASES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<NewReleasesIcon />} label="4. GitHub Releases" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        GitHub leverages Git tags to create <strong>Releases</strong>. This provides a user-friendly interface for downloading binaries, viewing changelogs, and notifying contributors.
                    </Typography>
                    <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                        <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>Push your tag to GitHub first.</Typography></Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>Navigate to the "Releases" section in your repository.</Typography></Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>Draft a new release and select your tag.</Typography></Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}><CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 20 }} /><Typography sx={{ color: BRAND.white, fontSize: 13 }}>Attach compiled assets (JARs, EXEs) and write your release notes.</Typography></Box>
                        </Stack>
                    </Paper>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Versioning Best Practices" />
                    <Grid container spacing={1.5}>
                        {[
                            "Use Semantic Versioning (SemVer): vMAJOR.MINOR.PATCH.",
                            "Prefer Annotated tags for public releases to include metadata.",
                            "Never move a tag; if code changes, create a new version (e.g., v1.0.1).",
                            "Automate tagging in your CI/CD pipeline after successful builds.",
                            "Include detailed release notes to help users understand changes.",
                            "Tag your hotfixes immediately to track stability in production."
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} key={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/devops-hub"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to DevOps Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Want to tag a commit from yesterday? You don't need to checkout the branch. Just provide the commit hash at the end of the command: <code>git tag -a v1.0 9fceb02</code>.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
